/**
 * PlotlyChart — shared wrapper for react-plotly.js charts.
 *
 * Replaces Plotly's native tooltip with a custom React tooltip that:
 *  - Follows the mouse cursor
 *  - Flips left when it would overflow the right viewport edge
 *  - Clamps to the bottom of the viewport
 *  - Applies consistent dark/light shell styling
 *  - Hides all Plotly hover artifacts (hoverlayer) via scoped CSS
 *
 * Props:
 *   data, layout, style, useResizeHandler, onClick — passed through to <Plot>
 *   config            — merged over defaults { displayModeBar: false, responsive: true }
 *   className         — extra class on the <Plot> element
 *   isDark            — boolean; drives tooltip shell bg/border colour
 *   onHoverData       — (plotlyHoverEvent) => payload | null
 *                       Return null to suppress the tooltip for that point.
 *   renderTooltip     — (payload, isDark) => JSX  (tooltip inner content)
 *   tooltipWidth      — number, default 220 (used for flip calculation)
 */

import React, { useRef, useState } from "react";
import Plot from "react-plotly.js";

const DEFAULT_CONFIG = { displayModeBar: false, responsive: true };

const PlotlyChart = ({
  data,
  layout,
  config,
  style,
  className,
  useResizeHandler,
  onClick,
  isDark = false,
  onHoverData,
  renderTooltip,
  tooltipWidth = 220,
  ...rest
}) => {
  const uid = useRef(`pchart-${Math.random().toString(36).slice(2, 10)}`).current;
  const scopeClass = uid;
  const [tip, setTip] = useState(null); // { x, y, payload }

  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const handleHover = (d) => {
    if (!onHoverData || !d.points?.length) return;
    const payload = onHoverData(d);
    if (payload != null) {
      setTip({ x: d.event.clientX, y: d.event.clientY, payload });
    }
  };

  return (
    <div className={scopeClass} style={{ position: "relative" }}>
      {/* Suppress Plotly's own hover layer for this chart instance only */}
      <style>{`.${scopeClass} .hoverlayer { display: none; }`}</style>

      <Plot
        className={className}
        data={data}
        layout={layout}
        config={mergedConfig}
        style={style}
        useResizeHandler={useResizeHandler}
        onHover={handleHover}
        onUnhover={() => setTip(null)}
        onClick={onClick}
        {...rest}
      />

      {tip && renderTooltip && (() => {
        const TIP_W = tooltipWidth;
        const left =
          tip.x + 16 + TIP_W < window.innerWidth
            ? tip.x + 16
            : tip.x - TIP_W - 12;
        const top = Math.min(tip.y - 16, window.innerHeight - 120);
        return (
          <div
            style={{
              position: "fixed",
              left,
              top,
              zIndex: 1000,
              pointerEvents: "none",
              width: TIP_W,
            }}
            className={`rounded-xl border shadow-lg text-[11px] ${
              isDark
                ? "bg-fill-deep-surface border-edge-dialog-border text-ink-gray-300"
                : "bg-fill-white border-edge-gray-200 text-ink-gray-700"
            }`}
          >
            {renderTooltip(tip.payload, isDark)}
          </div>
        );
      })()}
    </div>
  );
};

export default PlotlyChart;
